/**
 * Emergent Marketplace — Phase 3
 * VendorEarnings.jsx
 * Architectural Layer: Presentation → Vendor
 *
 * Full earnings view: summary cards, monthly bar chart, payout history table.
 * Design: Precise financial register — deep slate, warm white, muted gold.
 * Font: Libre Baskerville (numbers/headings) + Barlow (UI)
 */

import { useState, useEffect } from "react";
import {
  DollarSign, TrendingUp, TrendingDown, Clock,
  CheckCircle, AlertCircle, Download, ChevronDown, ArrowUpRight
} from "lucide-react";
import { apiFetch } from "./api";
import { EarningsBySource } from "../../components/marketplace/EarningsBySource";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Barlow:wght@300;400;500;600&display=swap');

  .ve-root {
    --slate:      #1e2430;
    --slate-mid:  #3a4458;
    --slate-light:#6a7890;
    --gold:       #c09840;
    --gold-light: #e0ba60;
    --gold-bg:    #fdf9ee;
    --white:      #ffffff;
    --off-white:  #f8f7f4;
    --border:     #e4e0d8;
    --green:      #1a6a40;
    --green-bg:   #eaf5f0;
    --green-border:#b0d8c0;
    --amber:      #a06818;
    --amber-bg:   #fef8ea;
    --red:        #a02828;
    font-family: 'Barlow', sans-serif;
    background: var(--off-white);
    color: var(--slate);
    min-height: 100vh;
  }

  .ve-header {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 20px 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .ve-header h1 {
    font-family: 'Libre Baskerville', serif;
    font-size: 22px;
    font-weight: 700;
    color: var(--slate);
    margin: 0 0 2px;
  }

  .ve-header p { font-size: 13px; color: var(--slate-light); margin: 0; }

  /* ── Summary cards ───────────────────────────────────────────────────── */
  .ve-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 14px;
    padding: 24px 28px 0;
  }

  .ve-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 20px;
  }

  .ve-shop-heading {
    font-family: 'Literata', serif; font-size: 15px; color: #1a1a16;
    margin: 4px 28px 10px; display: flex; flex-direction: column; gap: 2px;
  }
  .ve-shop-heading span { font-family: 'Source Sans 3', sans-serif; font-size: 12px; color: #8a8a80; }

  .ve-card-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--slate-light);
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .ve-card-value {
    font-family: 'Libre Baskerville', serif;
    font-size: 30px;
    font-weight: 700;
    color: var(--slate);
    line-height: 1;
    margin-bottom: 8px;
  }

  .ve-card-sub {
    font-size: 12px;
    color: var(--slate-light);
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .ve-trend-up   { color: var(--green); }
  .ve-trend-down { color: var(--red); }

  /* ── Monthly chart ───────────────────────────────────────────────────── */
  .ve-chart-section {
    margin: 24px 28px 0;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: hidden;
  }

  .ve-chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px 12px;
    border-bottom: 1px solid var(--border);
  }

  .ve-chart-title {
    font-family: 'Libre Baskerville', serif;
    font-size: 16px;
    font-weight: 700;
    color: var(--slate);
    margin: 0;
  }

  .ve-chart-legend {
    display: flex;
    gap: 16px;
    font-size: 11px;
    color: var(--slate-light);
  }

  .ve-legend-dot {
    display: inline-block;
    width: 8px; height: 8px;
    border-radius: 2px;
    margin-right: 5px;
  }

  /* SVG bar chart */
  .ve-chart-body { padding: 20px; overflow-x: auto; }

  .ve-bar-chart {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    height: 160px;
    min-width: max-content;
  }

  .ve-bar-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 48px;
  }

  .ve-bars {
    display: flex;
    align-items: flex-end;
    gap: 3px;
    height: 130px;
  }

  .ve-bar {
    width: 16px;
    border-radius: 4px 4px 0 0;
    transition: opacity 0.2s;
    cursor: pointer;
  }

  .ve-bar:hover { opacity: 0.75; }
  .ve-bar.gross { background: var(--border); }
  .ve-bar.net   { background: var(--gold); }

  .ve-bar-label {
    font-size: 11px;
    color: var(--slate-light);
    font-weight: 500;
    white-space: nowrap;
  }

  .ve-bar-amount {
    font-size: 10px;
    color: var(--gold);
    font-weight: 700;
  }

  /* ── Payout history table ─────────────────────────────────────────────── */
  .ve-table-section {
    margin: 20px 28px 40px;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: hidden;
  }

  .ve-table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px 12px;
    border-bottom: 1px solid var(--border);
  }

  .ve-table-title {
    font-family: 'Libre Baskerville', serif;
    font-size: 16px;
    font-weight: 700;
    color: var(--slate);
    margin: 0;
  }

  .ve-export-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: none;
    font-family: 'Barlow', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: var(--slate-mid);
    cursor: pointer;
    transition: border-color 0.15s;
  }

  .ve-export-btn:hover { border-color: var(--slate-mid); }

  table.ve-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  .ve-table th {
    text-align: left;
    padding: 10px 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--slate-light);
    border-bottom: 1px solid var(--border);
    background: var(--off-white);
  }

  .ve-table td {
    padding: 13px 20px;
    border-bottom: 1px solid #f0ede8;
    color: var(--slate);
  }

  .ve-table tr:last-child td { border-bottom: none; }
  .ve-table tr:hover td { background: var(--off-white); }

  .ve-table .amount  { font-family: 'Libre Baskerville', serif; font-weight: 700; }
  .ve-table .muted   { color: var(--slate-light); }
  .ve-table .fee     { color: var(--red); font-size: 12px; }
  .ve-table .net-amt { color: var(--green); }

  .ve-payout-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .ve-payout-transferred { background: var(--green-bg); color: var(--green); border: 1px solid var(--green-border); }
  .ve-payout-pending     { background: var(--amber-bg); color: var(--amber); }
  .ve-payout-failed      { background: #fdf0f0; color: var(--red); }

  .ve-stripe-link {
    font-size: 11px;
    color: var(--gold);
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 3px;
    cursor: pointer;
  }

  .ve-empty-row td {
    text-align: center;
    padding: 40px;
    color: var(--slate-light);
    font-size: 14px;
  }
`;

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_SUMMARY = {
  lifetime_gross:    3155.80,
  lifetime_fees:     157.79,
  lifetime_net:      2998.01,
  current_month_net: 412.00,
  last_month_net:    380.00,
  pending_transfer:  87.50,
  order_count:       38,
  payout_count:      28,
};

const MOCK_MONTHLY = [
  { label: "Oct 25", gross: 280, net: 266 },
  { label: "Nov 25", gross: 420, net: 399 },
  { label: "Dec 25", gross: 650, net: 617 },
  { label: "Jan 26", gross: 310, net: 294 },
  { label: "Feb 26", gross: 400, net: 380 },
  { label: "Mar 26", gross: 434, net: 412 },
];

const MOCK_PAYOUTS = [
  { id: "p1", created_at: "2026-03-12", gross_sales: 434, platform_fee: 21.70, net_payout: 412.30, status: "transferred", stripe_transfer_id: "tr_3OaB..." },
  { id: "p2", created_at: "2026-02-10", gross_sales: 400, platform_fee: 20.00, net_payout: 380.00, status: "transferred", stripe_transfer_id: "tr_3O9K..." },
  { id: "p3", created_at: "2026-01-08", gross_sales: 310, platform_fee: 15.50, net_payout: 294.50, status: "transferred", stripe_transfer_id: "tr_3O8P..." },
  { id: "p4", created_at: "2025-12-11", gross_sales: 650, platform_fee: 32.50, net_payout: 617.50, status: "transferred", stripe_transfer_id: "tr_3O7M..." },
  { id: "p5", created_at: "2025-11-09", gross_sales: 420, platform_fee: 21.00, net_payout: 399.00, status: "transferred", stripe_transfer_id: "tr_3O6N..." },
];

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

// ─── Bar chart ────────────────────────────────────────────────────────────────

function BarChart({ data }) {
  const maxVal = Math.max(...data.map(d => d.gross), 1);
  return (
    <div className="ve-bar-chart">
      {data.map((d, i) => (
        <div key={i} className="ve-bar-group">
          <div className="ve-bar-amount">${d.net}</div>
          <div className="ve-bars">
            <div
              className="ve-bar gross"
              style={{ height: `${(d.gross / maxVal) * 110}px` }}
              title={`Gross: $${d.gross}`}
            />
            <div
              className="ve-bar net"
              style={{ height: `${(d.net / maxVal) * 110}px` }}
              title={`Net: $${d.net}`}
            />
          </div>
          <div className="ve-bar-label">{d.label}</div>
        </div>
      ))}
    </div>
  );
}


/** Amounts that will come off the next payout — never a mystery deduction. */
function OwedDeductions({ ledger }) {
  const owed = (ledger.entries || []).filter(
    e => e.status === "owed" && e.direction === "debit"
  );
  if (!owed.length) return null;
  return (
    <div className="ve-table-section" data-testid="owed-deductions">
      <div className="ve-table-header">
        <p className="ve-table-title">Coming off your next payout</p>
        <span style={{ fontSize: 13, fontWeight: 700 }}>
          ${Number(ledger.owed || 0).toFixed(2)}
        </span>
      </div>
      <div style={{ padding: "4px 0" }}>
        {owed.map(e => (
          <div
            key={e.id}
            style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              gap: 12, padding: "11px 20px", borderTop: "1px solid var(--border)",
              fontSize: 13,
            }}
            data-testid={`owed-row-${e.id}`}
          >
            <span>{e.description || e.type}</span>
            <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              −${Number(e.amount || 0).toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VendorEarnings() {
  const [summary, setSummary] = useState({ 
    lifetime_net: 0, 
    lifetime_gross: 0, 
    lifetime_fees: 0, 
    current_month_net: 0, 
    last_month_net: 0, 
    pending_transfer: 0, 
    order_count: 0, 
    payout_count: 0 
  });
  const [monthly, setMonthly] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [ledger, setLedger] = useState({ entries: [], owed: 0 });
  const [gifts, setGifts] = useState({ entries: [], lifetime_total: 0, this_month_total: 0, total: 0 });

  useEffect(() => {
    Promise.all([
      apiFetch("/vendor/earnings/summary").catch(() => summary),
      apiFetch("/vendor/earnings/monthly").catch(() => []),
      apiFetch("/vendor/earnings/payouts").catch(() => []),
      apiFetch("/vendor/ledger").catch(() => ({ entries: [], owed: 0 })),
      apiFetch("/session-feedback/received").catch(() => ({ entries: [], lifetime_total: 0, this_month_total: 0, total: 0 })),
    ]).then(([s, m, p, l, g]) => { setSummary(s); setMonthly(m); setPayouts(p); setLedger(l); setGifts(g); });
  }, []);

  const exportPayoutsCsv = () => {
    const rows = [["Date", "Gross sales", "Platform fee", "Net payout", "Status", "Transfer"]];
    payouts.forEach(p => rows.push([
      formatDate(p.created_at), (p.gross_sales || 0).toFixed(2), (p.platform_fee || 0).toFixed(2),
      (p.net_payout || 0).toFixed(2), p.status || "", p.stripe_transfer_id || "",
    ]));
    gifts.entries.forEach(g => rows.push([
      formatDate(g.created_at), (g.amount || 0).toFixed(2), "0.00",
      (g.amount || 0).toFixed(2), `${g.kind} from ${g.from_name}`, "direct to your bank",
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `earnings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const monthPct = summary.last_month_net > 0
    ? ((summary.current_month_net - summary.last_month_net) / summary.last_month_net * 100).toFixed(0)
    : null;

  return (
    <>
      <style>{styles}</style>
      <div className="ve-root">
        <div className="ve-header">
          <div>
            <h1>Earnings</h1>
            <p>{summary.order_count} orders · {summary.payout_count} payouts received</p>
          </div>
        </div>

        <EarningsBySource />

        {/* Everything below this line is SHOP money only — it reads vendor_payouts and orders.
            Unlabelled, its four tiles looked like the whole picture, which is how a practitioner
            taking sessions came to believe Earnings was broken (owner, Jun 2026). */}
        <p className="ve-shop-heading" data-testid="earnings-shop-heading">
          Shop orders and payouts
          <span>Products only. Sessions, events and workshops are in the panel above.</span>
        </p>

        <div className="ve-cards">
          <div className="ve-card">
            <div className="ve-card-label"><DollarSign size={12} /> This month</div>
            <div className="ve-card-value">${(summary.current_month_net || 0).toFixed(2)}</div>
            {monthPct !== null && (
              <div className={`ve-card-sub ${Number(monthPct) >= 0 ? "ve-trend-up" : "ve-trend-down"}`}>
                {Number(monthPct) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {Math.abs(Number(monthPct))}% vs last month
              </div>
            )}
          </div>
          <div className="ve-card">
            <div className="ve-card-label"><CheckCircle size={12} /> Lifetime net</div>
            <div className="ve-card-value">${(summary.lifetime_net || 0).toFixed(2)}</div>
            <div className="ve-card-sub">
              After ${(summary.lifetime_fees || 0).toFixed(2)} in platform fees
            </div>
          </div>
          <div className="ve-card">
            <div className="ve-card-label"><Clock size={12} /> Pending</div>
            <div className="ve-card-value">${(summary.pending_transfer || 0).toFixed(2)}</div>
            <div className="ve-card-sub">In transit to your bank</div>
          </div>
          <div className="ve-card">
            <div className="ve-card-label"><ArrowUpRight size={12} /> Gross sales</div>
            <div className="ve-card-value">${(summary.lifetime_gross || 0).toFixed(2)}</div>
            <div className="ve-card-sub">Before fees</div>
          </div>
        </div>

        <OwedDeductions ledger={ledger} />

        {/* Monthly chart */}
        <div className="ve-chart-section">
          <div className="ve-chart-header">
            <p className="ve-chart-title">Monthly breakdown</p>
            <div className="ve-chart-legend">
              <span><span className="ve-legend-dot" style={{ background: "#e4e0d8" }} />Gross</span>
              <span><span className="ve-legend-dot" style={{ background: "#c09840" }} />Net</span>
            </div>
          </div>
          <div className="ve-chart-body">
            <BarChart data={monthly} />
          </div>
        </div>

        {/* Payout history */}
        <div className="ve-table-section">
          <div className="ve-table-header">
            <p className="ve-table-title">Payout history</p>
            <button className="ve-export-btn" onClick={exportPayoutsCsv} data-testid="earnings-export-csv">
              <Download size={13} /> Export CSV
            </button>
          </div>
          <table className="ve-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Gross sales</th>
                <th>Platform fee</th>
                <th>Net payout</th>
                <th>Status</th>
                <th>Transfer</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? (
                <tr className="ve-empty-row">
                  <td colSpan={6}>No payouts yet. Complete your first sale to see history here.</td>
                </tr>
              ) : (
                payouts.map(p => (
                  <tr key={p.id}>
                    <td className="muted">{formatDate(p.created_at)}</td>
                    <td className="amount">${p.gross_sales.toFixed(2)}</td>
                    <td className="fee">−${p.platform_fee.toFixed(2)}</td>
                    <td className="amount net-amt">${p.net_payout.toFixed(2)}</td>
                    <td>
                      <span className={`ve-payout-status ve-payout-${p.status}`}>
                        {p.status === "transferred" && <CheckCircle size={10} />}
                        {p.status === "pending"     && <Clock size={10} />}
                        {p.status === "failed"      && <AlertCircle size={10} />}
                        {p.status}
                      </span>
                    </td>
                    <td>
                      {p.stripe_transfer_id ? (
                        <span className="ve-stripe-link">
                          {p.stripe_transfer_id.slice(0, 12)}… <ArrowUpRight size={10} />
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Tips & donations — destination charges, so they never appear in payouts */}
        <div className="ve-table-section" data-testid="tips-donations-section">
          <div className="ve-table-header">
            <p className="ve-table-title">Tips &amp; donations received</p>
            <span style={{ fontSize: 12, color: "#8a8880" }}>
              ${(gifts.this_month_total || 0).toFixed(2)} this month · ${(gifts.lifetime_total || 0).toFixed(2)} lifetime
            </span>
          </div>
          <table className="ve-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>From</th>
                <th>Amount</th>
                <th>Where it went</th>
              </tr>
            </thead>
            <tbody>
              {(gifts.entries || []).length === 0 ? (
                <tr className="ve-empty-row">
                  <td colSpan={5}>
                    No tips or donations yet. Clients can tip after a session and donate any time
                    from your practitioner card.
                  </td>
                </tr>
              ) : (
                gifts.entries.map(g => (
                  <tr key={g.id} data-testid={`gift-row-${g.id}`}>
                    <td className="muted">{formatDate(g.created_at)}</td>
                    <td style={{ textTransform: "capitalize" }}>{g.kind}</td>
                    <td>{g.from_name}</td>
                    <td className="amount net-amt">${(g.amount || 0).toFixed(2)}</td>
                    <td className="muted">Paid straight to your bank — no platform fee</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>
    </>
  );
}
